"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAverageAllocationInPeriod = exports.updateAllocationsInPeriod = exports.updateAllocationPercent = exports.getAdjustedPercent = exports.calculateMaxLimit = exports.sortAllAscProjectAllocations = exports.assignPriority = exports.roundProjectAllocationPercents = exports.roundAllocationsToTotal = exports.removeUseAllocationPercent = exports.getLatestAllocationPercent = exports.ALLOCATION_DIFF_THRESHOLD = exports.AllocationPriority = void 0;
const constants_1 = require("../constants");
const errors_1 = require("../errors");
const month_year_utils_1 = require("../month-year.utils");
const number_utils_1 = require("../number.utils");
var AllocationPriority;
(function (AllocationPriority) {
    AllocationPriority["LOW"] = "LOW";
    AllocationPriority["MEDIUM"] = "MEDIUM";
    AllocationPriority["HIGH"] = "HIGH";
})(AllocationPriority || (exports.AllocationPriority = AllocationPriority = {}));
const ALLOCATION_PRIORITY_ORDER = [AllocationPriority.HIGH, AllocationPriority.MEDIUM, AllocationPriority.LOW];
exports.ALLOCATION_DIFF_THRESHOLD = 0.01;
const getLatestAllocationPercent = (ascProjectMonthAllocation) => ascProjectMonthAllocation.useAllocationPercent ?? ascProjectMonthAllocation.allocationPercent;
exports.getLatestAllocationPercent = getLatestAllocationPercent;
const removeUseAllocationPercent = (ascProjectIdToPmas) => Object.fromEntries(Object.entries(ascProjectIdToPmas).map(([ascProjectId, ascProjectAllocations]) => [
    ascProjectId,
    ascProjectAllocations.map((ascProjectAllocation) => ({
        ...ascProjectAllocation,
        useAllocationPercent: null,
    })),
]));
exports.removeUseAllocationPercent = removeUseAllocationPercent;
/**
 * Rounds allocation percentages and adjusts them to ensure the total equals at most 100%.
 * If rounding causes the total to exceed 100%, the largest allocations are reduced.
 * This prevents negative non-project percentages and ensures consistent rounding behavior.
 *
 * @param allocations - Array of raw allocation percentages (can have decimals)
 * @returns Array of rounded percentages that sum to at most 100%
 */
const roundAllocationsToTotal = (allocations) => {
    // Round all allocations
    const rounded = allocations.map((allocation) => (0, number_utils_1.roundToNearest)(allocation, constants_1.USER_ROUNDING_NUM_DECIMALS));
    let total = rounded.reduce((sum, val) => sum + val, 0);
    // If total exceeds 100%, adjust the largest allocations down
    if (total > 100) {
        const excess = total - 100;
        const originalTotal = total;
        // Create array of indices sorted by value (descending) to process largest first
        const indexedValues = rounded.map((value, index) => ({ value, index })).sort((a, b) => b.value - a.value);
        // Iterate through allocations, applying proportional reductions until excess is consumed
        // Calculate what percentage each allocation represents of the original total
        let remainingExcess = excess;
        for (const item of indexedValues) {
            if (remainingExcess <= 0)
                break;
            if (rounded[item.index] <= 0)
                continue;
            // Calculate what percentage this allocation represents of the original total
            const proportion = item.value / originalTotal;
            // Calculate proportional reduction: this allocation's share of the excess
            const proportionalReduction = proportion * excess;
            // Round the reduction
            const reduction = Math.round(proportionalReduction);
            // Don't reduce more than the allocation value or more than we still need to reduce
            const finalReduction = Math.min(reduction, rounded[item.index], remainingExcess);
            rounded[item.index] -= finalReduction;
            total -= finalReduction;
            remainingExcess -= finalReduction;
        }
        // The proportional pass places nothing when each allocation's share of the excess rounds to zero
        // — 18 projects at 6% apiece each owe 0.44 of an 8 point excess. Drain whatever is left one
        // rounding step at a time, largest allocation first, revisiting the list until the total lands on
        // 100. Taking the whole remainder out of a single allocation instead zeroes that project out and
        // still leaves the total above 100.
        const step = 10 ** -constants_1.USER_ROUNDING_NUM_DECIMALS;
        while (remainingExcess > 0) {
            const reducibleIndexes = rounded
                .map((value, index) => ({ value, index }))
                .filter((item) => item.value > 0)
                .sort((a, b) => b.value - a.value)
                .map((item) => item.index);
            if (reducibleIndexes.length === 0) {
                break;
            }
            for (const index of reducibleIndexes) {
                if (remainingExcess <= 0) {
                    break;
                }
                const reduction = Math.min(step, rounded[index], remainingExcess);
                rounded[index] -= reduction;
                total -= reduction;
                remainingExcess -= reduction;
            }
        }
    }
    return rounded;
};
exports.roundAllocationsToTotal = roundAllocationsToTotal;
/**
 * The single normalization primitive for a contributor's per-project allocation percentages.
 * Rounds the raw percents as a group (see {@link roundAllocationsToTotal}) so they can never sum
 * past 100, and derives the residual non-project percent (100 − Σ).
 *
 * Every path that turns raw per-project allocations into stored/whole percents (wage-report upload,
 * report generation, the client allocation modal) funnels through this so rounding stays consistent
 * and non-project time can never go negative. Rounding each project independently can push the total
 * to 101% even when the raw values sum to exactly 100% (e.g. two 12.5% projects each round up to
 * 13%), which drives non-project time negative and lets the overshoot survive into a capitalized
 * percentage above 100%.
 *
 * @param rawProjectPercents - Raw per-project allocation percentages (may have decimals), assumed
 *   non-negative; input order is preserved in `projectPercents`.
 */
const roundProjectAllocationPercents = (rawProjectPercents) => {
    const projectPercents = (0, exports.roundAllocationsToTotal)(rawProjectPercents);
    const nonProjectPercent = 100 - projectPercents.reduce((sum, percent) => sum + percent, 0);
    return { projectPercents, nonProjectPercent };
};
exports.roundProjectAllocationPercents = roundProjectAllocationPercents;
const assignPriority = (ascProjectIdToCurrentPma, ascProjectIdToDeltaAveragePercent) => {
    const result = {};
    Object.entries(ascProjectIdToCurrentPma).forEach(([ascProjectId, ascProjectAllocations]) => {
        const delta = ascProjectIdToDeltaAveragePercent.get(ascProjectId);
        if (typeof delta !== 'number') {
            throw (0, errors_1.createError)('UpdateAllocationsError', `ASC Project ${ascProjectId} does not have a delta. This should not happen.`);
        }
        if (delta < 0) {
            result[ascProjectId] = ascProjectAllocations.map((allocation) => {
                const { allocationPercent } = allocation;
                if (allocationPercent > 0) {
                    return {
                        ...allocation,
                        priority: AllocationPriority.HIGH,
                    };
                }
                return {
                    ...allocation,
                    priority: AllocationPriority.LOW,
                };
            });
        }
        else {
            result[ascProjectId] = ascProjectAllocations.map((allocation, index) => {
                const { allocationPercent } = allocation;
                if (allocationPercent > 0) {
                    return {
                        ...allocation,
                        priority: AllocationPriority.HIGH,
                    };
                }
                const prev = index > 0 ? ascProjectAllocations[index - 1] : undefined;
                const next = index < ascProjectAllocations.length - 1 ? ascProjectAllocations[index + 1] : undefined;
                const prevHasAllocation = prev && prev.allocationPercent > 0;
                const nextHasAllocation = next && next.allocationPercent > 0;
                if (prevHasAllocation || nextHasAllocation) {
                    return {
                        ...allocation,
                        priority: AllocationPriority.MEDIUM,
                    };
                }
                return {
                    ...allocation,
                    priority: AllocationPriority.LOW,
                };
            });
        }
    });
    return result;
};
exports.assignPriority = assignPriority;
// Sort ASC projects from the lowest delta to the highest delta
// When it comes to positive delta ASC projects, bring ASC projects with 0% months first.
const sortAllAscProjectAllocations = (allAscProjectAllocationsMap, ascProjectIdToDeltaAveragePercent) => {
    const sortedAllAscProjectAllocations = Object.entries(allAscProjectAllocationsMap).sort(([ascProjectIdA], [ascProjectIdB]) => {
        const deltaA = ascProjectIdToDeltaAveragePercent.get(ascProjectIdA);
        const deltaB = ascProjectIdToDeltaAveragePercent.get(ascProjectIdB);
        if (typeof deltaA !== 'number' || typeof deltaB !== 'number') {
            throw (0, errors_1.createError)('UpdateAllocationsError', `ASC Project ${!deltaA ? ascProjectIdA : ascProjectIdB} does not have a delta average percent. This should not happen.`);
        }
        if (deltaA <= 0 || deltaB <= 0) {
            return deltaA - deltaB;
        }
        const ascProjectAllocationsA = allAscProjectAllocationsMap[ascProjectIdA];
        const ascProjectAllocationsB = allAscProjectAllocationsMap[ascProjectIdB];
        const numberOfZeroPercentMonthsInAscProjectA = ascProjectAllocationsA.filter((allocation) => allocation.allocationPercent === 0).length;
        const numberOfZeroPercentMonthsInAscProjectB = ascProjectAllocationsB.filter((allocation) => allocation.allocationPercent === 0).length;
        return numberOfZeroPercentMonthsInAscProjectB - numberOfZeroPercentMonthsInAscProjectA;
    });
    return sortedAllAscProjectAllocations.map(([ascProjectId]) => ascProjectId);
};
exports.sortAllAscProjectAllocations = sortAllAscProjectAllocations;
const calculateMaxLimit = ({ ascProjectId, ascProjectIdToPmas, month, nonProjectTime, year, }) => {
    const otherAscProjectAllocationsInSameMonth = Object.entries(ascProjectIdToPmas).reduce((acc, [id, ascProjectAllocations]) => {
        if (id === ascProjectId) {
            // skip the current project
            return acc;
        }
        // TODO use a map to improve performance
        const ascProjectAllocationInSameMonth = ascProjectAllocations.find((a) => a.month === month && a.year === year);
        if (!ascProjectAllocationInSameMonth) {
            throw (0, errors_1.createError)(`ASC Project allocation not found for month:${month} and year:${year}, ascProjectId ${ascProjectId}`);
        }
        acc += (0, exports.getLatestAllocationPercent)(ascProjectAllocationInSameMonth);
        return acc;
    }, 0);
    return Math.abs(100 - (otherAscProjectAllocationsInSameMonth + nonProjectTime));
};
exports.calculateMaxLimit = calculateMaxLimit;
const getAdjustedPercent = ({ ascProjectAllocation, currentPriorityAllocations, currentPriorityTotal, deltaTotal, maxLimit, }) => Math.max(0, Math.min(maxLimit, (0, exports.getLatestAllocationPercent)(ascProjectAllocation) +
    deltaTotal *
        // If the current priority total is 0, it means that all the allocations in this priority are 0%.
        // In this case, we should distribute the delta evenly.
        (currentPriorityTotal === 0
            ? 1 / currentPriorityAllocations.length
            : (0, exports.getLatestAllocationPercent)(ascProjectAllocation) / currentPriorityTotal)));
exports.getAdjustedPercent = getAdjustedPercent;
const updateAllocationPercent = ({ ascProjectId, ascProjectIdToPmas, newAverage, newNonProjectTime, priority, shouldFill, }) => {
    const currentAscProjectAllocations = ascProjectIdToPmas[ascProjectId];
    if (!currentAscProjectAllocations) {
        throw (0, errors_1.createError)('UpdateAllocationsError', `ASC Project ${ascProjectId} does not have any allocations. This should not happen.`);
    }
    const newTotal = newAverage * currentAscProjectAllocations.length;
    const originalTotal = currentAscProjectAllocations.reduce((a, b) => a + (0, exports.getLatestAllocationPercent)(b), 0);
    const deltaTotal = newTotal - originalTotal;
    const currentPriorityAllocations = currentAscProjectAllocations.filter((ascProjectAllocation) => ascProjectAllocation.priority === priority);
    if (currentPriorityAllocations.length === 0) {
        // nothing to do
        return { done: false, ascProjectIdToPmas };
    }
    const currentPriorityTotal = currentPriorityAllocations.reduce((a, b) => a + (0, exports.getLatestAllocationPercent)(b), 0);
    const newAllocations = currentAscProjectAllocations.map((ascProjectAllocation) => {
        if (ascProjectAllocation.priority !== priority) {
            return { ...ascProjectAllocation, maxLimitReached: false };
        }
        const { month, year } = ascProjectAllocation;
        const maxLimit = (0, exports.calculateMaxLimit)({
            month,
            year,
            nonProjectTime: newNonProjectTime,
            ascProjectIdToPmas,
            ascProjectId,
        });
        const adjustedPercent = shouldFill
            ? maxLimit
            : (0, exports.getAdjustedPercent)({
                currentPriorityAllocations,
                currentPriorityTotal,
                deltaTotal,
                maxLimit,
                ascProjectAllocation,
            });
        const maxLimitReached = Math.abs(adjustedPercent - maxLimit) < exports.ALLOCATION_DIFF_THRESHOLD;
        return {
            ...ascProjectAllocation,
            useAllocationPercent: adjustedPercent,
            maxLimitReached,
        };
    });
    ascProjectIdToPmas[ascProjectId] = newAllocations;
    const updatedTotal = newAllocations.reduce((a, b) => a + (0, exports.getLatestAllocationPercent)(b), 0);
    const diff = updatedTotal - newTotal;
    const done = Math.abs(diff) < exports.ALLOCATION_DIFF_THRESHOLD;
    const allMonthsHitMaxLimit = newAllocations
        .filter((allocation) => allocation.priority === priority)
        .every((allocation) => allocation.maxLimitReached);
    if (!done && !allMonthsHitMaxLimit) {
        // re-running allocation to fill up the remaining
        return (0, exports.updateAllocationPercent)({
            ascProjectIdToPmas,
            newAverage,
            newNonProjectTime,
            priority,
            ascProjectId,
            shouldFill,
        });
    }
    return {
        done,
        ascProjectIdToPmas,
    };
};
exports.updateAllocationPercent = updateAllocationPercent;
/**
 * Clamp every month's `useAllocationPercent` into the valid [0, 100] range. A single month's
 * allocation can exceed 100 because {@link calculateMaxLimit} bounds each month by
 * `Math.abs(100 - (otherAllocations + nonProjectTime))`, which grows past 100 when non-project time
 * is negative (e.g. project averages that sum past 100% due to rounding). A monthly allocation
 * above 100% is meaningless and is rejected when the PMA is persisted, so this guarantees callers
 * always receive a valid range.
 */
const clampUseAllocationPercentInPeriod = (ascProjectIdToPmas) => Object.fromEntries(Object.entries(ascProjectIdToPmas).map(([ascProjectId, pmas]) => [
    ascProjectId,
    pmas.map((pma) => ({
        ...pma,
        useAllocationPercent: pma.useAllocationPercent == null
            ? pma.useAllocationPercent
            : Math.min(100, Math.max(0, pma.useAllocationPercent)),
    })),
]));
/**
 * Will attempt to use {@link updateAllocationsUsingProportionateMethod} to update the allocations. There is not always a solution to the allocation
 * problem. In this case, we will assign the average percent to all the months uniformly.
 */
const updateAllocationsInPeriod = ({ ascProjectIdToPmas, newAverages, newNonProjectTime, }) => {
    // validate input
    // Non-project time is the residual `100 - Σ project percents`, so a negative value means the
    // project percents summed past 100% for that contributor-month. The sum check below cannot catch
    // that on its own — it asserts projects + non-project = 100, and 101 + (-1) reconciles to 100,
    // which is exactly how the overshoot in OPS-843 reached stored data and surfaced as a capitalized
    // percentage above 100%.
    if (newNonProjectTime < 0) {
        throw (0, errors_1.createError)('UpdateAllocationsError', `Non project time cannot be negative, meaning the project allocations sum to more than 100%. Non project time: ${newNonProjectTime}`);
    }
    if (newAverages.length !== Object.keys(ascProjectIdToPmas).length) {
        throw (0, errors_1.createError)('UpdateAllocationsError', `Number of ASC projects in the new averages does not match the number of ASC projects in the current allocations. new averages: ${newAverages.length}, current allocations: ${ascProjectIdToPmas.length}`);
    }
    const perAscProjectNumPmas = new Set(Object.entries(ascProjectIdToPmas).map(([_key, pmas]) => pmas.length));
    if (perAscProjectNumPmas.size > 1) {
        throw (0, errors_1.createError)('UpdateAllocationsError', 'Not all ASC project have the same number of PMAs');
    }
    const newAllocationSum = newAverages.reduce((acc, average) => {
        acc += average.allocationPercent;
        return acc;
    }, newNonProjectTime);
    if (Math.abs(newAllocationSum - 100) > exports.ALLOCATION_DIFF_THRESHOLD) {
        throw (0, errors_1.createError)('UpdateAllocationsError', `The sum of new averages and the new non project time is not 100. Sum: ${newAllocationSum}`);
    }
    // DO NOT REMOVE THIS LINE, IT IS ESSENTIAL FOR HOW THIS ALGORITHM WORKS!
    const ascProjectIdToPmasWithoutUseAllocationPercent = (0, exports.removeUseAllocationPercent)(ascProjectIdToPmas);
    // create maps for efficient access during computation
    const ascProjectIdToNewAveragePercent = new Map(newAverages.map((average) => [average.ascProjectId, average.allocationPercent]));
    const ascProjectIdToOriginalAveragePercent = new Map(Object.entries(ascProjectIdToPmasWithoutUseAllocationPercent).map(([ascProjectId, pmas]) => {
        const originalPercentTotal = pmas.reduce((a, b) => a + b.allocationPercent, 0);
        const ascProjectAverage = originalPercentTotal / pmas.length;
        return [ascProjectId, ascProjectAverage];
    }));
    const ascProjectIdToDeltaAveragePercent = new Map([...ascProjectIdToNewAveragePercent.entries()].map(([ascProjectId, newAverage]) => {
        const originalAverage = ascProjectIdToOriginalAveragePercent.get(ascProjectId);
        if (typeof originalAverage !== 'number') {
            throw (0, errors_1.createError)('UpdateAllocationsError', `ASC Project ${ascProjectId} does not have an original average percent. This should not happen.`);
        }
        return [ascProjectId, newAverage - originalAverage];
    }));
    // if all the deltas are within min diff threshold, then we can just return the current allocations
    const areAllDeltaZero = [...ascProjectIdToDeltaAveragePercent.values()].every((delta) => Math.abs(delta) < exports.ALLOCATION_DIFF_THRESHOLD);
    if (areAllDeltaZero) {
        return { newAscProjectIdToPmas: ascProjectIdToPmasWithoutUseAllocationPercent, fallbackError: null };
    }
    try {
        return {
            newAscProjectIdToPmas: clampUseAllocationPercentInPeriod(updateAllocationsUsingProportionateMethod({
                newNonProjectTime,
                ascProjectIdToDeltaAveragePercent: ascProjectIdToDeltaAveragePercent,
                ascProjectIdToNewAveragePercent: ascProjectIdToNewAveragePercent,
                ascProjectIdToPmas: ascProjectIdToPmasWithoutUseAllocationPercent,
            })),
            fallbackError: null,
        };
    }
    catch (error) {
        // fallback to uniform allocations
        return {
            newAscProjectIdToPmas: clampUseAllocationPercentInPeriod(updateAllocationsUsingUniformMethod({
                ascProjectIdToNewAveragePercent: ascProjectIdToNewAveragePercent,
                ascProjectIdToPmas: ascProjectIdToPmasWithoutUseAllocationPercent,
            })),
            fallbackError: error instanceof Error ? error : new Error(String(error)),
        };
    }
};
exports.updateAllocationsInPeriod = updateAllocationsInPeriod;
/**
 * Steps to calculate the new allocations is described in the notion doc
 * https://www.notion.so/neo-tax/Soft-Cap-Allocation-Edit-Strategy-54d37ce0116a401a9ad29a819788351c?pvs=4
 */
const updateAllocationsUsingProportionateMethod = ({ ascProjectIdToDeltaAveragePercent, ascProjectIdToNewAveragePercent, ascProjectIdToPmas, newNonProjectTime, }) => {
    const ascProjectIdToPmaWithPriority = (0, exports.assignPriority)(ascProjectIdToPmas, ascProjectIdToDeltaAveragePercent);
    const sortedAscProjectIds = (0, exports.sortAllAscProjectAllocations)(ascProjectIdToPmaWithPriority, ascProjectIdToDeltaAveragePercent);
    let currentAllAscProjectAllocations = ascProjectIdToPmaWithPriority;
    sortedAscProjectIds.map((ascProjectId, index) => {
        const isLastAscProject = index === sortedAscProjectIds.length - 1;
        let currentPriorityIndex = 0;
        let currentDone = false;
        while (!currentDone && currentPriorityIndex < ALLOCATION_PRIORITY_ORDER.length) {
            const priority = ALLOCATION_PRIORITY_ORDER[currentPriorityIndex];
            const newAverage = ascProjectIdToNewAveragePercent.get(ascProjectId);
            if (typeof newAverage !== 'number') {
                throw (0, errors_1.createError)('UpdateAllocationsError', `ASC Project ${ascProjectId} does not have a new average percent. This should not happen.`);
            }
            const allocationResults = (0, exports.updateAllocationPercent)({
                ascProjectIdToPmas: ascProjectIdToPmaWithPriority,
                newAverage,
                newNonProjectTime,
                priority,
                ascProjectId,
                shouldFill: isLastAscProject,
            });
            currentAllAscProjectAllocations = allocationResults.ascProjectIdToPmas;
            currentDone = allocationResults.done;
            currentPriorityIndex += 1;
        }
        return {
            ascProjectId,
            done: currentDone,
        };
    });
    const currentAllAscProjectAllocationsEntries = Object.entries(currentAllAscProjectAllocations);
    const months = currentAllAscProjectAllocationsEntries[0][1];
    for (const { month, year } of months) {
        const totalAllocationAcrossAllProjectsForMonthYear = currentAllAscProjectAllocationsEntries.reduce((acc, [_ascProjectId, pmasWithPriority]) => {
            // find allocation for that month and year
            // TODO use a month year key and map to improve performance
            const ascProjectAllocation = pmasWithPriority.find((allocation) => allocation.month === month && allocation.year === year);
            if (!ascProjectAllocation) {
                throw (0, errors_1.createError)('UpdateAllocationsError', `ASC Project allocation not found for month:${month} and year:${year}`);
            }
            acc += (0, exports.getLatestAllocationPercent)(ascProjectAllocation);
            return acc;
        }, 0);
        if (Math.abs(totalAllocationAcrossAllProjectsForMonthYear + newNonProjectTime - 100) > exports.ALLOCATION_DIFF_THRESHOLD) {
            throw (0, errors_1.createError)('UpdateAllocationsError', `The sum of allocations for month ${month} and year ${year} is not 100. Sum: ${totalAllocationAcrossAllProjectsForMonthYear}`);
        }
    }
    return currentAllAscProjectAllocations;
};
const updateAllocationsUsingUniformMethod = ({ ascProjectIdToNewAveragePercent, ascProjectIdToPmas, }) => {
    for (const [ascProjectId, averagePercent] of ascProjectIdToNewAveragePercent.entries()) {
        const pmas = ascProjectIdToPmas[ascProjectId];
        if (!pmas) {
            throw (0, errors_1.createError)('UpdateAllocationsError', `ASC Project ${ascProjectId} does not have any allocations. This should not happen.`);
        }
        const updatedPmas = pmas.map((pma) => ({
            ...pma,
            useAllocationPercent: averagePercent,
        }));
        ascProjectIdToPmas[ascProjectId] = updatedPmas;
    }
    return ascProjectIdToPmas;
};
/**
 * HACK: this is an approximation at best, because we do not consider the number of work days in each month while averaging
 * the allocation percentages. However, because using constant non-project turns pmas into a relative and approximate value this
 * is considered good enough for now.
 *
 * @returns the float average percent without any rounding
 */
const getAverageAllocationInPeriod = (ascProjectMonthAllocations, monthYearPeriod) => {
    const fixedMonthYearPeriod = (0, month_year_utils_1.applyCompletedEndMonthYearRule)(monthYearPeriod);
    const { endMonthYear, startMonthYear } = fixedMonthYearPeriod;
    const numberOfMonths = (0, month_year_utils_1.getNumberOfMonthsInPeriod)(fixedMonthYearPeriod);
    const pmasInPeriod = ascProjectMonthAllocations.filter((pma) => (0, month_year_utils_1.isMonthYearInRange)(pma, startMonthYear, endMonthYear));
    const averagePercent = pmasInPeriod.reduce((acc, pma) => acc + (pma.useAllocationPercent ?? pma.allocationPercent), 0) / numberOfMonths;
    return averagePercent;
};
exports.getAverageAllocationInPeriod = getAverageAllocationInPeriod;
